import { supabase } from '@/core/database/supabase';
import type { 
  ProjectPhase, 
  PhaseStats, 
  PhaseFilters 
} from '../types/project-phases.types';

export const ProjectPhasesService = {
  /**
   * Danh sách giai đoạn dự án
   */
  async getPhases(companyId: string, filters: PhaseFilters = {}): Promise<{ data: ProjectPhase[], total: number }> {
    let query = supabase
      .from('project_phases')
      .select(`
        *,
        project:projects(id, name),
        parent_phase:project_phases!parent_phase_id(id, name)
      `, { count: 'exact' })
      .eq('company_id', companyId);

    if (filters.status && filters.status !== 'all') query = query.eq('status_code', filters.status);
    if (filters.type && filters.type !== 'all') query = query.eq('phase_type', filters.type);
    if (filters.projectId) query = query.eq('project_id', filters.projectId);
    if (filters.search) {
      query = query.or(`name.ilike.%${filters.search}%,code.ilike.%${filters.search}%`);
    }

    query = query.order('display_order', { ascending: true });

    const { data, error, count } = await query;

    if (error) throw error;
    return {
      data: (data || []) as ProjectPhase[],
      total: count || 0
    };
  },

  /**
   * Chi tiết giai đoạn
   */
  async getPhaseDetail(id: string): Promise<ProjectPhase> {
    const { data, error } = await supabase
      .from('project_phases')
      .select(`
        *,
        project:projects(id, name),
        parent_phase:project_phases!parent_phase_id(id, name)
      `)
      .eq('id', id)
      .single();

    if (error) throw error;
    return data as ProjectPhase;
  },

  /**
   * Thống kê tổng quan giai đoạn
   */
  async getPhaseStats(companyId: string, projectId?: string): Promise<PhaseStats> {
    let query = supabase.from('project_phases').select('status_code, progress_percent, payment_milestone, planned_end_date, actual_end_date').eq('company_id', companyId);
    if (projectId) query = query.eq('project_id', projectId);

    const { data, error } = await query;
    if (error) throw error;

    const completed = data.filter(p => p.status_code === 'completed' || p.status_code === 'closed').length;
    const avgProgress = data.length > 0 ? data.reduce((sum, p) => sum + (p.progress_percent || 0), 0) / data.length : 0;
    const paymentCount = data.filter(p => p.payment_milestone).length;
    
    const today = new Date();
    const delayed = data.filter(p => {
      if (p.status_code === 'completed' || p.status_code === 'closed') return false;
      if (!p.planned_end_date) return false;
      return new Date(p.planned_end_date) < today;
    }).length;

    return {
      totalPhases: data.length,
      completedPhases: completed,
      avgProgress,
      delayedPhases: delayed,
      paymentMilestones: paymentCount,
    };
  },

  /**
   * CRUD
   */
  async createPhase(data: Partial<ProjectPhase>): Promise<ProjectPhase> {
    const { data: result, error } = await supabase
      .from('project_phases')
      .insert([data])
      .select()
      .single();

    if (error) throw error;
    return result as ProjectPhase;
  },

  async updatePhase(id: string, updates: Partial<ProjectPhase>): Promise<void> {
    const { error } = await supabase
      .from('project_phases')
      .update(updates)
      .eq('id', id);

    if (error) throw error;
  }
};
